import React, { useState, useEffect, useRef } from 'react';
import { object, func, bool, string } from 'prop-types';

import { CloneObject, CompareDates } from '../../../utils/helpers';
import { getDateNow } from '../../../utils/convert';
import { isEmptyString } from '../../../utils/validator';
import { ALERT_TYPE, MASK_FORMAT, PATTERNS, ShowToastMessage, useForm } from '@laramie/ui';
import { VALIDATION_MESSAGE } from '../../../consts/validationMessage';
import { useFormErrors } from '../../hooks/useFormErrors';
import { useLista } from '../../hooks/useLista';
import DataTaggerFormRedux from '../DataTaggerFormRedux';
import { FormError, InputFormat, InputLista, FormLabel, Modal } from '../../common';
import { TIPS } from '../../../consts/tips';
import InputMonthYear from './components/InputMonthYear';


const MedioPagoModal = (props) => {

  const entityInit = {
    id: 0,
    idTipoPersona: 0,
    idPersona: 0,
    idTipoMedioPago: 0,
    titular: '',
    numero: '',
    alias: '',
    idTipoTarjeta: 0,
    idMarcaTarjeta: 0,
    fechaVencimiento: null,
    idEntidadBancaria: 0
  };

  //hooks
  const [state, setState] = useState({
    entity: entityInit,
    showInfo: false
  });
  // El titular de antes de una tecla muerta o un IME: si la letra compuesta no es válida, el campo vuelve a él.
  const titularAntesDeComponer = useRef('');

  const mount = () => {
    setState(prevState => {
      return {...prevState, entity: props.data.entity };
    });
    formSet({
      idTipoPersona: props.data.entity.idTipoPersona,
      idPersona: props.data.entity.idPersona,
      idTipoMedioPago: props.data.entity.idTipoMedioPago,
      titular: props.data.entity.titular,
      numero: props.data.entity.numero,
      alias: props.data.entity.alias,
      idTipoTarjeta: props.data.entity.idTipoTarjeta,
      idMarcaTarjeta: props.data.entity.idMarcaTarjeta,
      fechaVencimiento: props.data.entity.fechaVencimiento,
      idEntidadBancaria: props.data.entity.idEntidadBancaria
    });
  }
  useEffect(mount, [props.data.entity]);

  const [, getRowLista] = useLista({
    listas: ['TipoMedioPago', 'TipoTarjeta', 'MarcaTarjeta', 'EntidadBancaria'],
    onLoaded: (listas, isSuccess, error) => {
      if (isSuccess) {
        setState(prevState => ({...prevState}));
      }
      else {
        ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error);
      }
    },
    memo: {
      key: 'TipoMedioPago_TipoTarjeta_MarcaTarjeta_EntidadBancaria',
      timeout: 0
    }
  });

  const [ formValues, formHandle, , formSet ] = useForm({
    idTipoMedioPago: 0,
    titular: '',
    numero: '',
    alias: '',
    idTipoTarjeta: 0,
    idMarcaTarjeta: 0,
    fechaVencimiento: null,
    idEntidadBancaria: 0
  });

  const { errorOf, validate } = useFormErrors(getErrors, getFormatErrors);

  const disabledCampos = props.disabled || props.data.entity.tieneDebitoAutomatico === true;

  //handles
  const handleClickAceptar = () => {
    if (validate()) {

      let row = CloneObject(state.entity);
      row.idTipoMedioPago = parseInt(formValues.idTipoMedioPago);
      row.titular = formValues.titular;
      row.numero = formValues.numero;
      if (formValues.idTipoMedioPago === 611) { //CBU
        row.alias = formValues.alias;
        row.idEntidadBancaria = formValues.idEntidadBancaria;
      }
      else {
        row.alias = '';
        row.idEntidadBancaria = 0;
      }
      if (formValues.idTipoMedioPago === 610) { //Tarjeta Crédito/Débito
        row.idTipoTarjeta = !isEmptyString(formValues.idTipoTarjeta) ? parseInt(formValues.idTipoTarjeta) : 0;
        row.idMarcaTarjeta = !isEmptyString(formValues.idMarcaTarjeta) ? parseInt(formValues.idMarcaTarjeta) : 0;
        row.fechaVencimiento = formValues.fechaVencimiento;
      }
      else {
        row.idTipoTarjeta = 0;
        row.idMarcaTarjeta = 0;
        row.fechaVencimiento = null;
      }

      props.onConfirm(row);
    };
  };

  //funciones
  function getErrors() {

    const errors = {};

    if (formValues.idTipoMedioPago <= 0) errors.idTipoMedioPago = VALIDATION_MESSAGE.REQUIRED_OPTION;
    if (formValues.titular.length <= 0) errors.titular = VALIDATION_MESSAGE.REQUIRED;
    if (formValues.numero.length <= 0) errors.numero = VALIDATION_MESSAGE.REQUIRED;

    if (formValues.idTipoMedioPago === 611) { //CBU
      if (formValues.idEntidadBancaria <= 0) errors.idEntidadBancaria = VALIDATION_MESSAGE.REQUIRED_OPTION;
    }

    if (formValues.idTipoMedioPago === 610) { //Tarjeta Crédito/Débito
      if (formValues.idTipoTarjeta <= 0) errors.idTipoTarjeta = VALIDATION_MESSAGE.REQUIRED_OPTION;
      if (formValues.idMarcaTarjeta <= 0) errors.idMarcaTarjeta = VALIDATION_MESSAGE.REQUIRED_OPTION;
      if (formValues.fechaVencimiento === null) errors.fechaVencimiento = VALIDATION_MESSAGE.REQUIRED_DATE;
    }

    return errors;
  }

  function getFormatErrors() {

    const errors = {};

    if (props.disabled) return errors;

    if (formValues.idTipoMedioPago === 611 && formValues.numero.length > 0 && formValues.numero.length !== 22) {
      errors.numero = 'Debe tener 22 dígitos';
    }

    if (formValues.idTipoMedioPago === 610) {
      //el registro del archivo de débito mide 100 caracteres con el número adentro: otro largo
      //aborta la generación del archivo completo, no sólo la cuota de esta tarjeta
      if (formValues.numero.length > 0 && formValues.numero.length !== 16) {
        errors.numero = 'Debe tener 16 dígitos';
      }
      if (formValues.fechaVencimiento !== null && CompareDates(getDateNow(), formValues.fechaVencimiento) > 0) {
        errors.fechaVencimiento = 'No puede estar vencida';
      }
    }

    return errors;
  }

  const getDescTipoMedioPago = (id) => {
    const row = getRowLista('TipoMedioPago', id);
    return (row) ? row.nombre : '';
  }

  function ToggleAccordionInfo() {
    setState(prevState => {
      return {...prevState, showInfo: !prevState.showInfo};
    });
  }
  
  const accordionClose = <span className="material-symbols-outlined search-i">chevron_right</span>
  const accordionOpen = <span className="material-symbols-outlined search-i">expand_more</span> 

  return (
    <>

    <Modal
      size="lg"
      title="Medio de pago"
      tipName={TIPS.NAME.MODALS.MEDIO_PAGO_MODAL}
      onEscape={() => props.onDismiss()}
      body={(<>
            <div className="row">
                <div className="col-12 col-lg-4">
                    <FormLabel htmlFor="idTipoMedioPago" required>Tipo de medio de pago</FormLabel>
                    <InputLista
                        name="idTipoMedioPago"
                        placeholder=""
                        className={`form-control ${errorOf('idTipoMedioPago') ? 'is-invalid' : ''}`}
                        value={ formValues.idTipoMedioPago }
                        onChange={({target}) => {
                          formSet({...formValues,
                            idTipoMedioPago: target.value,
                            numero: ''
                          });
                        }}
                        disabled={disabledCampos}
                        lista="TipoMedioPago"
                    />
                    <FormError message={errorOf('idTipoMedioPago')} />
                </div>
                <div className="col-12 col-lg-4">
                    <FormLabel htmlFor="titular" required>Titular</FormLabel>
                    <input
                        name="titular"
                        type="text"
                        placeholder=""
                        className={`form-control ${errorOf('titular') ? 'is-invalid' : ''}`}
                        value={ formValues.titular }
                        onChange={({target, nativeEvent}) =>{
                          // Una tecla muerta (´ ¨ en una Mac) o un IME escribe primero una marca provisoria que el
                          // filtro no admite, y sacarla corta la composición: la letra llegaría sin su tilde. Mientras
                          // se compone, el valor queda como lo deja el navegador; el filtro se aplica al terminar.
                          if (nativeEvent.isComposing || target.validity.valid) {
                              formSet({...formValues, titular: target.value});
                          }
                        }}
                        onCompositionStart={() => { titularAntesDeComponer.current = formValues.titular; }}
                        onCompositionEnd={({target}) => {
                          formSet({...formValues, titular: target.validity.valid ? target.value : titularAntesDeComponer.current});
                        }}
                        maxLength="250"
                        disabled={disabledCampos}
                        pattern={PATTERNS.onlyLettersWithSpace}
                    />
                    <FormError message={errorOf('titular')} />
                </div>
                <div className="col-12 col-lg-4">
                    <FormLabel htmlFor="numero" required>Número</FormLabel>
                    <InputFormat
                        name="numero"
                        placeholder=""
                        className={`form-control ${errorOf('numero') ? 'is-invalid' : ''}`}
                        mask={(formValues.idTipoMedioPago === 610/*Tarjeta Credito/Debito*/) ? MASK_FORMAT.TARJETA :
                              (formValues.idTipoMedioPago === 611/*CBU/CVU*/) ? MASK_FORMAT.CBU : '*'}
                        maskPlaceholder={null}
                        value={ formValues.numero }
                        onChange={ formHandle }
                        disabled={disabledCampos}
                    />
                    <FormError message={errorOf('numero')} />
                </div>
            </div>
            <div className="row">

            {(formValues.idTipoMedioPago === 611/*CBU*/ &&
            <div className="row">
               <div className="col-12 col-lg-6">
                  <FormLabel htmlFor="idEntidadBancaria" required>Entidad</FormLabel>
                  <InputLista
                        name="idEntidadBancaria"
                        placeholder=""
                        className={`form-control ${errorOf('idEntidadBancaria') ? 'is-invalid' : ''}`}
                        value={ formValues.idEntidadBancaria }
                        onChange={ formHandle }
                        disabled={disabledCampos}
                        lista="EntidadBancaria"
                  />
                  <FormError message={errorOf('idEntidadBancaria')} />
                </div>
                <div className="col-12 col-lg-6">
                    <label htmlFor="alias" className="form-label">Alias</label>
                    <input
                        name="alias"
                        type="text"
                        placeholder=""
                        className="form-control"
                        value={ formValues.alias }
                        onChange={ formHandle }
                        maxLength="50"
                        disabled={props.disabled}
                    />
                    <FormError />
                </div>
            </div>
            )}
            </div>
            {(formValues.idTipoMedioPago === 610/*Tarjeta Crédito/Débito*/ &&
            <div className="row">
                <div className="col-12 col-lg-4">
                    <FormLabel htmlFor="idTipoTarjeta" required>Tipo de tarjeta</FormLabel>
                    <InputLista
                        name="idTipoTarjeta"
                        placeholder=""
                        className={`form-control ${errorOf('idTipoTarjeta') ? 'is-invalid' : ''}`}
                        value={ formValues.idTipoTarjeta }
                        onChange={ formHandle }
                        disabled={disabledCampos}
                        lista="TipoTarjeta"
                    />
                    <FormError message={errorOf('idTipoTarjeta')} />
                </div>
                <div className="col-12 col-lg-3">
                    <FormLabel htmlFor="idMarcaTarjeta" required>Marca de tarjeta</FormLabel>
                    <InputLista
                        name="idMarcaTarjeta"
                        placeholder=""
                        className={`form-control ${errorOf('idMarcaTarjeta') ? 'is-invalid' : ''}`}
                        value={ formValues.idMarcaTarjeta }
                        onChange={ formHandle }
                        disabled={disabledCampos}
                        lista="MarcaTarjeta"
                    />
                    <FormError message={errorOf('idMarcaTarjeta')} />
                </div>
                <div className="col-6 col-lg-3">
                    <FormLabel htmlFor="fechaVencimiento" required>Fecha vencimiento</FormLabel>
                    <InputMonthYear
                      name="fechaVencimiento"
                      placeholder="MM/AAAA"
                      className={`form-control ${errorOf('fechaVencimiento') ? 'is-invalid' : ''}`}
                      value={formValues.fechaVencimiento}
                      onChange={formHandle}
                      disabled={disabledCampos}
                    />
                    <FormError message={errorOf('fechaVencimiento')} />
                </div>
            </div>
            )}

            <div className="row">
                <div className="m-top-20 mb-3 col-12">
                  <div className='accordion-header'>
                    <div className='row'>
                      <div className="col-12" onClick={() => ToggleAccordionInfo()}>
                        <div className='accordion-header-title'>
                          {(state.showInfo) ? accordionOpen : accordionClose}
                          <h3 className={state.showInfo ? 'active' : ''}>Información adicional</h3>
                        </div>
                      </div>
                    </div>
                  </div>
                  {state.showInfo &&
                  <div className='accordion-body'>
                    <DataTaggerFormRedux
                        title="Información adicional de Medio de Pago"
                        processKey={props.processKey}
                        entidad="MedioPago"
                        idEntidad={state.entity.id}
                        disabled={props.disabled}
                    />
                  </div>
                  }
                </div>                
            </div>
      </>)}
      footer={!props.disabled ? (<>
              <button className="btn btn-outline-primary" data-dismiss="modal" onClick={ (event) => props.onDismiss() }>Cancelar</button>
              <button className="btn btn-primary" data-dismiss="modal" onClick={ (event) => handleClickAceptar() }>Aceptar</button>
      </>) : (
              <button className="btn back-button" data-dismiss="modal" onClick={ (event) => props.onDismiss() }>Volver</button>
      )}
      footerClassName={props.disabled ? 'f-align' : ''}
    />

    </>
  );
}

MedioPagoModal.propTypes = {
  processKey: string.isRequired,
  disabled: bool,
  data: object.isRequired,
  onConfirm: func.isRequired,
  onDismiss: func.isRequired,
};

MedioPagoModal.defaultProps = {
  disabled: false
};

export default MedioPagoModal;